import { deepClone } from 'fast-json-patch';
import {
  IdAndTreeExpression,
  IdExpressionToken,
  IdIncompleteExpressionToken,
  IdNotTreeExpression,
  IdOrTreeExpression,
  IdTreeExpression,
} from './entities';
import { doesExist } from '@benzinga/utils';
import { SearchItem } from '@benzinga/search-modules';

export function newNotNode(uuid?: string): IdNotTreeExpression {
  return {
    expr: undefined,
    isGhost: false,
    operator: 'not',
    parentUuid: undefined,
    uuid: uuid ?? window.crypto.randomUUID(),
  };
}
export function newAndNode(uuid?: string): IdAndTreeExpression {
  return {
    exprs: [],
    isGhost: false,
    operator: 'and',
    parentUuid: undefined,
    uuid: uuid ?? window.crypto.randomUUID(),
  };
}
export function newOrNode(uuid?: string): IdOrTreeExpression {
  return {
    exprs: [],
    isGhost: false,
    operator: 'or',
    parentUuid: undefined,
    uuid: uuid ?? window.crypto.randomUUID(),
  };
}
export function newExpressionNode<T>(item: SearchItem<T>, uuid?: string): IdExpressionToken<T> {
  return {
    isGhost: false,
    operator: 'expression',
    parentUuid: undefined,
    uuid: uuid ?? window.crypto.randomUUID(),
    ...item,
  };
}
export function newIncompleteExpressionNode<T>(
  moduleId: string,
  fallbackExpression?: IdExpressionToken<T>,
  uuid?: string,
): IdIncompleteExpressionToken<T> {
  if (fallbackExpression && fallbackExpression.moduleId !== moduleId) {
    throw new Error(
      `newIncompleteExpressionNode Error: fallbackExpression moduleId ${fallbackExpression.moduleId} does not match moduleId ${moduleId}`,
    );
  }
  return {
    fallbackExpression,
    isGhost: false,
    moduleId,
    operator: 'incomplete_expression',
    parentUuid: undefined,
    uuid: uuid ?? window.crypto.randomUUID(),
  };
}

export function tryFindNode(root: IdTreeExpression | undefined, id: string): IdTreeExpression | undefined {
  if (!root) {
    return undefined;
  }
  if (root.uuid === id) {
    return root;
  }
  switch (root.operator) {
    case 'and':
    case 'or':
      return root.exprs.map(e => tryFindNode(e, id)).find(doesExist);
    case 'not':
      return tryFindNode(root.expr, id);
    default:
      return undefined;
  }
}

export function findNode(root: IdTreeExpression, id: string): IdTreeExpression {
  const result = tryFindNode(root, id);
  if (result == null) {
    throw new Error(`findNode Error: Could not find node with id ${id}\n${JSON.stringify(root)}`);
  }
  return result;
}
export function tryGetNodeDepth(root: IdTreeExpression | undefined, id: string): number | undefined {
  if (!root) {
    return undefined;
  }
  if (root.uuid === id) {
    return 0;
  }
  switch (root.operator) {
    case 'and':
    case 'or':
      return root.exprs
        .map(e => {
          const result = tryGetNodeDepth(e, id);
          return result != null ? result + 1 : result;
        })
        .find(doesExist);
    case 'not': {
      const result = tryGetNodeDepth(root.expr, id);
      return result != null ? result + 1 : result;
    }
    default:
      return undefined;
  }
}

export function getNodeDepth(root: IdTreeExpression, id: string): number {
  const result = tryGetNodeDepth(root, id);
  if (result == null) {
    throw new Error(`getNodeDepth Error: Could not find node with id ${id}\n${JSON.stringify(root)}`);
  }
  return result;
}

export function getParentRelativeIndex(
  root: IdTreeExpression,
  node: IdTreeExpression,
  parent: IdTreeExpression,
): number {
  switch (parent.operator) {
    case 'and':
    case 'or': {
      const idx = parent.exprs.findIndex(e => e.uuid === node.uuid);
      if (idx === -1) {
        throw new Error(
          `getParentRelativeIndex Error: Parent ${parent} does not contain node ${node}\n${JSON.stringify(root)}`,
        );
      }
      return idx;
    }
    case 'not':
      return 0;
    default:
      throw new Error(
        `getParentRelativeIndex Error: Node ${node} parent ${parent} cannot be of expression type\n${JSON.stringify(
          root,
        )}`,
      );
  }
}

export function detachNode(
  root: IdTreeExpression,
  node: IdTreeExpression,
  parent: IdTreeExpression | undefined,
): IdTreeExpression | undefined {
  if (node.parentUuid && parent) {
    const parentRelativeIdx = getParentRelativeIndex(root, node, parent);

    switch (parent.operator) {
      case 'and':
      case 'or':
        parent.exprs.splice(parentRelativeIdx, 1);
        break;
      case 'not':
        parent.expr = undefined;
        break;
      default:
        throw new Error(
          `detachNode Error: Node ${node} parent ${parent} cannot be of expression type\n${JSON.stringify(root)}`,
        );
    }
    node.parentUuid = undefined;
    return root;
  } else if (root === node) {
    return undefined;
  } else {
    throw new Error(`detachNode Error: Node ${node} is not attached to a parent\n${JSON.stringify(root)}`);
  }
}

export function attachNode(
  root: IdTreeExpression,
  node: IdTreeExpression,
  parent: IdTreeExpression,
  parentRelativeIdx: number,
): IdTreeExpression {
  if (node.parentUuid) {
    throw new Error(
      `attachNode Error: Node ${node} is already attached to a parent, call detachNode first\n${JSON.stringify(root)}`,
    );
  }

  node.parentUuid = parent.uuid;

  switch (parent.operator) {
    case 'and':
    case 'or': {
      if (parentRelativeIdx === parent.exprs.length) {
        parent.exprs.push(node);
        break;
      }
      if (parentRelativeIdx > parent.exprs.length + 1) {
        throw new Error(
          `attachNode Error: Parent ${parent} cannot have a child at index ${parentRelativeIdx}\n${JSON.stringify(
            root,
          )}`,
        );
      }
      parent.exprs.splice(parentRelativeIdx, 0, node);
      break;
    }
    case 'not':
      if (parent.expr !== undefined) {
        throw new Error(`attachNode Error: NOT Parent ${parent} already has a child\n${JSON.stringify(root)}`);
      }
      if (parentRelativeIdx !== 0) {
        throw new Error(`attachNode Error: NOT Parent ${parent} can only have one child\n${JSON.stringify(root)}`);
      }
      parent.expr = node;
      break;
    default:
      throw new Error(
        `attachNode Error: Node ${node} parent ${parent} cannot be of expression type\n${JSON.stringify(root)}`,
      );
  }
  if (node === root) {
    return parent;
  } else {
    return root;
  }
}

export function getNodeList(root?: IdTreeExpression): IdTreeExpression[] {
  const list: IdTreeExpression[] = [];
  function traverse(root: IdTreeExpression) {
    switch (root.operator) {
      case 'and':
      case 'or':
        root.exprs.forEach(e => {
          list.push(e);
          traverse(e);
        });
        break;
      case 'not':
        if (root.expr) {
          list.push(root.expr);
          traverse(root.expr);
        }
        break;
      default:
        break;
    }
  }
  if (!root) {
    return list;
  }
  list.push(root);
  traverse(root);
  return list;
}

export function ghostNode(root: IdTreeExpression | undefined) {
  if (!root) {
    return;
  }
  root.isGhost = true;
}

export function unGhostNode(root: IdTreeExpression | undefined) {
  if (!root) {
    return;
  }
  root.isGhost = false;
}

export function ghostRoot(root: IdTreeExpression | undefined) {
  if (!root) {
    return;
  }
  ghostNode(root);
  switch (root.operator) {
    case 'and':
    case 'or':
      root.exprs.forEach(e => ghostRoot(e));
      break;
    case 'not':
      root.expr && ghostRoot(root.expr);
      break;
    default:
      break;
  }
}

export function unGhostRoot(root: IdTreeExpression | undefined) {
  if (!root) {
    return;
  }
  unGhostNode(root);
  switch (root.operator) {
    case 'and':
    case 'or':
      root.exprs.forEach(e => unGhostRoot(e));
      break;
    case 'not':
      root.expr && unGhostRoot(root.expr);
      break;
    default:
      break;
  }
}

export function cloneTree<T extends IdTreeExpression | undefined>(root: T): T {
  return deepClone(root) as T;
}

export function simplifyExpression(root: IdTreeExpression | undefined): IdTreeExpression | undefined {
  if (!root) {
    return root;
  }
  function simplify(root: IdTreeExpression, node: IdTreeExpression, parent?: IdTreeExpression): IdTreeExpression[] {
    parent && detachNode(root, node, parent);

    switch (node.operator) {
      case 'and':
      case 'or': {
        const new_exprs = [...node.exprs].flatMap(e => simplify(root, e, node));
        if (new_exprs.length <= 1) {
          ghostNode(new_exprs.at(0));
          return new_exprs;
        } else if (node.operator === parent?.operator) {
          new_exprs.forEach(e => {
            ghostNode(e);
          });
          return new_exprs;
        } else {
          new_exprs.forEach((e, idx) => {
            attachNode(root, e, node, idx);
          });
          return [node];
        }
      }
      case 'not': {
        const new_expr = node.expr ? simplify(root, node.expr, node) : [];
        if (new_expr.length === 0) {
          return [];
        }
        const not_children = new_expr.filter((e): e is IdNotTreeExpression => e.operator === 'not');
        const other_children = new_expr.filter(e => e.operator !== 'not');
        const result: IdTreeExpression[] = [];

        result.push(
          ...not_children.flatMap(e => {
            ghostNode(e);
            return e.expr ? [e.expr] : [];
          }),
        );
        if (other_children.length > 1) {
          const group = newOrNode();
          ghostNode(group);
          attachNode(root, group, node, 0);
          other_children.forEach((e, idx) => {
            attachNode(root, e, group, idx);
          });
          result.push(group);
        } else if (other_children.length > 0) {
          attachNode(root, other_children[0], node, 0);
          result.push(node);
        }

        return result;
      }
      case 'expression': {
        return [node];
      }
      case 'incomplete_expression': {
        if (node.fallbackExpression) {
          return [node.fallbackExpression];
        } else {
          return [];
        }
      }
    }
  }

  const result = simplify(root, root);

  if (result.length === 0) {
    return undefined;
  } else if (result.length === 1) {
    return result[0];
  } else {
    const group = newOrNode();
    ghostNode(group);
    result.forEach((e, idx) => {
      attachNode(root, e, group, idx);
    });
    return group;
  }
}
